import { DialogRoot } from "@radix-ui/themes";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../../hooks/useFetch";
import { FShift, ShiftBreak } from "../../../../../../types/types";
import { DialogContent } from "../../../../../../ui/Dialog";
import { Label } from "../../../../../../ui/Label/Label";
import { SelectButton } from "../../../../../../ui/SelectButton/SelectButton";
import { Button } from "../../../../../../ui/Button";
import DeleteModel from "./deleteBreak";
import OneSelector from "../../../../../../ui/OneSelector/OneSelector";
import { times } from "../../../../../../enums/times";
import { DateTime } from "luxon";
import { useAppStore } from "../../../../../../stores/RootStore";

type Props = {
  id: string;
  shiftId: string | undefined;
  isEditOpen: boolean;
  closeModal: Function;
  refreshModels: Function;
  shiftBreaks: ShiftBreak[] | undefined;
  currentShift: FShift;
};

const EditBreak = ({ id, shiftId, isEditOpen, closeModal, refreshModels, shiftBreaks, currentShift }: Props) => {
  const currentBreak = shiftBreaks?.find((model) => model.id === id);

  const { currentUser, currentSite } = useAppStore();
  const isRestricted = !currentUser?.attributes.admin && currentSite?.user_status == "user";

  const started = DateTime.fromISO(currentBreak?.attributes.started_at!).toFormat("HH:mm");
  const ended = DateTime.fromISO(currentBreak?.attributes.ended_at!).toFormat("HH:mm");

  const { reset, handleSubmit, control } = useForm({
    defaultValues: {
      break_start: times.find((time) => time.value === started),
      break_end: times.find((time) => time.value === ended),
    },
  });

  let newTimes: { value: string; label: string }[];
  const startShiftInSeconds = currentShift.start_at.toSeconds() - currentShift.start_at.startOf("day").toSeconds();
  const endShiftInSeconds = currentShift.end_at.toSeconds() - currentShift.end_at.startOf("day").toSeconds();
  const isNightShift = startShiftInSeconds > endShiftInSeconds ? true : false;
  if (!isNightShift) {
    newTimes = times.filter((time) => {
      const timeInSeconds =
        Number(time.value[0] + time.value[1]) * 60 * 60 + Number(time.value[3] + time.value[4]) * 60;
      if (timeInSeconds >= startShiftInSeconds && timeInSeconds <= endShiftInSeconds) return true;
      else return false;
    });
  } else {
    // Разбивка на два подмассива для сохранения порядка времен (от начала смены, до конца)
    const newTimes1 = times.filter((time) => {
      const timeInSeconds =
        Number(time.value[0] + time.value[1]) * 60 * 60 + Number(time.value[3] + time.value[4]) * 60;
      if (timeInSeconds >= startShiftInSeconds) return true;
      else return false;
    });
    const newTimes2 = times.filter((time) => {
      const timeInSeconds =
        Number(time.value[0] + time.value[1]) * 60 * 60 + Number(time.value[3] + time.value[4]) * 60;
      if (timeInSeconds <= endShiftInSeconds) return true;
      else return false;
    });
    newTimes = [...newTimes1, ...newTimes2];
  }

  const openStateChange = () => {
    if (isEditOpen) closeModal();
  };

  const [{ response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError }, doFetchItemUpdate] =
    useFetch(`/directories/working_shifts/${shiftId}/working_shift_breaks/${id}`);

  const onSubmit = (formData: any) => {
    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: {
          started_at: `${formData.break_start.value}:00`,
          ended_at: `${formData.break_end.value}:00`,
        },
      },
    });
    reset();
  };

  useEffect(() => {
    if (itemUpdateResponse) {
      refreshModels();
      openStateChange();
    }
  }, [itemUpdateResponse]);

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  function onItemDelete(): void {
    openDeleteModal();
  }

  return (
    <DialogRoot open={isEditOpen} onOpenChange={openStateChange}>
      <DialogContent style={{ maxWidth: 600, position: "fixed", top: "30%" }}>
        <div className="text-lg font-medium">{t("editing_break")}</div>
        <div className="text-sm">{t("adding_break_form_text")}</div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full items-end gap-4 mt-4">
            <div className="grid grid-cols-1 items-end w-full gap-4">
              <Controller
                name="break_start"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative flex flex-col space-y-2">
                    <Label>{t("break_start")}*</Label>
                    <SelectButton
                      disabled={isRestricted}
                      label={"Выберите время"}
                      searchable={true}
                      options={newTimes}
                      value={value ? newTimes.find((s) => s.value == String(value.value))! : null}
                      onChange={(e) => onChange(e)}
                    />
                  </div>
                )}
              />
              <Controller
                name="break_end"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative flex flex-col space-y-2">
                    <Label>{t("break_end")}*</Label>
                    <SelectButton
                      disabled={isRestricted}
                      label={"Выберите время"}
                      searchable={true}
                      options={newTimes}
                      value={value ? newTimes.find((s) => s.value == String(value.value))! : null}
                      onChange={(e) => onChange(e)}
                    />
                  </div>
                )}
              />
            </div>
            <div className="flex justify-between mt-4">
              <Button type="button" disabled={isRestricted} variant={"destructive"} size={"sm"} onClick={onItemDelete}>
                {t("delete")}
              </Button>
              <Button variant={"default"} size={"sm"}>
                {t("save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
      <DeleteModel
        id={id}
        shiftId={shiftId!}
        isDeleteOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        refreshModels={refreshModels}
        closeModalOnDelete={closeModal}
      />
    </DialogRoot>
  );
};

export default EditBreak;
